import styled from "@emotion/styled"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import * as regularIcons from "@fortawesome/pro-regular-svg-icons"
import * as solidIcons from "@fortawesome/pro-solid-svg-icons"
import * as lightIcons from "@fortawesome/pro-light-svg-icons"
import * as brandIcons from "@fortawesome/free-brands-svg-icons"
import VisuallyHidden from "@reach/visually-hidden"
import { getTextSize, getLineHeight, getColorFromProps } from "@ninjaone/utils"

const StyledIcon = styled(FontAwesomeIcon)`
  font-size: ${({ _size, fontSize }) => (fontSize ? fontSize : getTextSize(_size))};
  line-height: ${({ _size, fontSize }) => (!fontSize ? getLineHeight(_size) : "")};
  user-select: none;
  color: ${getColorFromProps()};
`

const createIcon = icon => ({ label, color, size = "md", fixedWidth = true, fontSize, ...restProps }) => (
  <>
    {label && <VisuallyHidden>{label}</VisuallyHidden>}
    <StyledIcon {...{ icon, _size: size, color, fixedWidth, fontSize, ...restProps }} />
  </>
)

function getOpenArrowIcon(direction, inverse) {
  switch (direction) {
    case "up":
      return inverse ? regularIcons.faChevronDown : regularIcons.faChevronUp
    case "right":
      return inverse ? regularIcons.faChevronDown : regularIcons.faChevronRight
    case "down":
      return inverse ? regularIcons.faChevronRight : regularIcons.faChevronDown
    case "left":
    default:
      return inverse ? regularIcons.faChevronDown : regularIcons.faChevronLeft
  }
}

export const OrganizationIcon = createIcon(regularIcons.faSitemap)
export const OrganizationIconLight = createIcon(lightIcons.faSitemap)
export const UserGeneralIcon = createIcon(regularIcons.faUserCog)
export const UserPermissionsIcon = createIcon(regularIcons.faLockOpen)
export const UserRoleMembersIcon = createIcon(regularIcons.faUsers)
export const UserRolesIcon = createIcon(regularIcons.faIdBadge)
export const DeleteIcon = createIcon(regularIcons.faTrashAlt)
export const RemoveIcon = createIcon(regularIcons.faTimes)
export const SearchIcon = createIcon(regularIcons.faSearch)
export const ShowOptionsIcon = createIcon(regularIcons.faEllipsisV)
export const SignOutIcon = createIcon(regularIcons.faSignOut)
export const SignOutIconLight = createIcon(lightIcons.faSignOut)
export const VerifiedIcon = createIcon(solidIcons.faBadgeCheck)
export const InputErrorIcon = createIcon(solidIcons.faExclamationCircle)
export const HelpIcon = createIcon(solidIcons.faQuestionCircle)
export const ExternalLinkIcon = createIcon(solidIcons.faExternalLink)
export const ExternalLinkIconLight = createIcon(lightIcons.faExternalLink)
export const ExclamationTriangle = createIcon(solidIcons.faExclamationTriangle)
export const CircleCheckIcon = createIcon(solidIcons.faCircleCheck)
export const InfoCircleIcon = createIcon(solidIcons.faInfoCircle)
export const RegularInfoCircleIcon = createIcon(regularIcons.faInfoCircle)
export const CaretUpIcon = createIcon(solidIcons.faCaretUp)
export const CaretDownIcon = createIcon(solidIcons.faCaretDown)
export const CaretRightIcon = createIcon(regularIcons.faAngleRight)
export const AngleDownIcon = createIcon(regularIcons.faAngleDown)
export const AngleUpIcon = createIcon(regularIcons.faAngleUp)
export const AngleRightIcon = createIcon(regularIcons.faAngleRight)
export const OpenArrowIcon = ({ direction = "right", inverse, ...rest }) =>
  createIcon(getOpenArrowIcon(direction, inverse))(rest)
export const SelectIcon = createIcon(solidIcons.faSort)
export const ArrowRightIcon = createIcon(regularIcons.faArrowRight)
export const ChevronRightIconLight = createIcon(lightIcons.faChevronRight)
export const Circle = createIcon(solidIcons.faCircle)
export const MinusCircle = createIcon(solidIcons.faMinusCircle)
export const ExclamationCircle = createIcon(solidIcons.faExclamationCircle)
export const QuestionCircle = createIcon(regularIcons.faQuestionCircle)
export const FileAltIconLight = createIcon(lightIcons.faFileAlt)
export const FolderIconLight = createIcon(lightIcons.faFolder)
export const FileAltIcon = createIcon(regularIcons.faFileAlt)
export const SlidersIcon = createIcon(regularIcons.faSlidersH)
export const InboxIcon = createIcon(regularIcons.faInbox)
export const PlayIcon = createIcon(solidIcons.faPlay)
export const PauseIcon = createIcon(solidIcons.faPause)
export const RedoIcon = createIcon(regularIcons.faRedo)
export const BoltIcon = createIcon(solidIcons.faBolt)
export const PencilIcon = createIcon(solidIcons.faPencil)
export const EllipsisVIcon = ShowOptionsIcon
export const EditIcon = createIcon(regularIcons.faEdit)
export const PlusIcon = createIcon(regularIcons.faPlus)
export const DevicesIcon = createIcon(regularIcons.faLaptop)
export const ListIcon = createIcon(regularIcons.faList)
export const EllipsisHIcon = createIcon(regularIcons.faEllipsisH)
export const RefreshIcon = createIcon(regularIcons.faSync)
export const CalendarIcon = createIcon(regularIcons.faCalendar)
export const UserPlusIcon = createIcon(regularIcons.faUserPlus)
export const GripVerticalIcon = createIcon(solidIcons.faGripVertical)
export const TicketIconLight = createIcon(lightIcons.faTicket)
export const TableColumnsIconLight = createIcon(lightIcons.faTableColumns)
export const DesktopIconLight = createIcon(lightIcons.faDesktop)
export const UserIconLight = createIcon(lightIcons.faUser)
export const FileChartPieIconLight = createIcon(lightIcons.faFileChartPie)
export const SiteMapIconLight = createIcon(lightIcons.faSitemap)
export const MemoCircleCheckIconLight = createIcon(lightIcons.faMemoCircleCheck)
export const CalendarCheckIconLight = createIcon(lightIcons.faCalendarCheck)
export const UserHelmetSafetyIconLight = createIcon(lightIcons.faUserHelmetSafety)
export const CloudIconLight = createIcon(lightIcons.faCloud)
export const MobileIconLight = createIcon(lightIcons.faMobile)
export const UserCrownIconLight = createIcon(lightIcons.faUserCrown)
export const QuestionCircleIconLight = createIcon(lightIcons.faQuestionCircle)
export const MegaphoneIconLight = createIcon(lightIcons.faMegaphone)
export const MemoIconLight = createIcon(lightIcons.faMemo)
export const PaperPlaneIconLight = createIcon(lightIcons.faPaperPlane)
export const FileLinesIconLight = createIcon(lightIcons.faFileLines)
export const ReceiptIconLight = createIcon(lightIcons.faReceipt)
export const AsteriskIcon = createIcon(regularIcons.faAsterisk)
export const RefreshLightIcon = createIcon(lightIcons.faSync)
export const DownloadIcon = createIcon(lightIcons.faArrowToBottom)
export const SettingsIcon = createIcon(lightIcons.faCog)
export const SortUpIcon = createIcon(lightIcons.faLongArrowUp)
export const SortDownIcon = createIcon(lightIcons.faLongArrowDown)
export const GlobeIconLight = createIcon(lightIcons.faGlobe)

///////////////////////////
// Custom SVGs
///////////////////////////

export const EmptyTableIcon = ({ colors: { shadow, box, border } }) => (
  <svg width="72px" height="46px" viewBox="0 0 72 46">
    <g id="User-Permissions" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="Do-Not-Configure---Inherited-Role-w/o-Orgs" transform="translate(-606.000000, -837.000000)">
        <g id="Group" transform="translate(606.000000, 838.000000)">
          <ellipse id="Oval" fill={shadow} fillRule="nonzero" cx="36" cy="37" rx="36" ry="8"></ellipse>
          <g transform="translate(10.000000, 0.000000)" id="Path" stroke={border}>
            <path d="M52,14.2182857 L40.5306087,1.40177143 C39.980087,0.528171429 39.1763478,0 38.3296522,0 L13.6703478,0 C12.8236522,0 12.019913,0.528171429 11.4693913,1.40065714 L0,14.2194 L0,24.5142857 L52,24.5142857 L52,14.2182857 Z"></path>
            <path
              d="M36.8668696,17.7516857 C36.8668696,15.9632571 37.9905217,14.4868286 39.3843478,14.4857143 L52,14.4857143 L52,34.6955143 C52,37.0611429 50.5078261,39 48.6652174,39 L3.33478261,39 C1.49217391,39 0,37.0600286 0,34.6955143 L0,14.4857143 L12.6156522,14.4857143 C14.0094783,14.4857143 15.1331304,15.9599143 15.1331304,17.7483429 L15.1331304,17.7728571 C15.1331304,19.5612857 16.2692174,21.0054 17.661913,21.0054 L34.338087,21.0054 C35.7307826,21.0054 36.8668696,19.5479143 36.8668696,17.7594857 L36.8668696,17.7516857 L36.8668696,17.7516857 Z"
              fill={box}
              fillRule="nonzero"
            ></path>
          </g>
        </g>
      </g>
    </g>
  </svg>
)

export const FilterIcon = ({ className }) => {
  return (
    <svg
      {...{ className }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 10"
      height="16px"
      width="16px"
      fill="currentColor"
    >
      <rect width="12" height="2" rx="0.5" />
      <rect x="2" y="4" width="8" height="2" rx="0.5" />
      <rect x="4" y="8" width="4" height="2" rx="0.5" />
    </svg>
  )
}

export const HyperVHostIcon = createIcon({
  prefix: "fac",
  iconName: "Hyper-V-Host",
  icon: [
    200,
    200,
    [],
    "019",
    "M71.37,13.62,2.51,23.09V80H71.37Zm-13.1,53.3H15.61V34.51l42.66-5.86Z M197.49,112.57v-80l-26.94,3.72V0L79.23,12.61V80h27v8.24H79.23v32.56H71.37V88.25H2.51v56.1l26.94,3.7v28.86l68.87,9.47V157.46l7.86,1.09v28.84L197.49,200V120.81H170.55v-8.24ZM92.33,24l65.12-9V66.92H92.33Zm26.94,56h51.28V49.5l13.84-1.91V99.48H170.55V88.25H119.27ZM92.33,101.35h65.12v51.06l-65.12-9Zm-76.72,0H58.27v37.44l-42.66-5.86Zm69.61,70-42.67-5.86V149.85l28.82,4V133.91h7.86v20.92l6,.82Zm99.17-37.44V185l-65.12-9V160.36l51.28,7.08V133.91Z",
  ],
})

export const HyperVGuestIcon = createIcon({
  prefix: "fac",
  iconName: "Hyper-V-Guest",
  icon: [
    200,
    200,
    [],
    "020",
    "M69.62,30.74V83.25H12.36V38.62l57.26-7.88M82,16.59,0,27.85V95.59H82v-79Z M69.62,117.73v51.53l-57.26-7.88V117.73H69.62M82,105.39H0v66.76L82,183.41v-78Z M187.64,117.73v67.73l-84-11.6V117.73h84M200,105.39H91.32v79.23l108.66,15V105.39Z M187.64,14.54V83.25h-84V26.14l84-11.6M200,.38l-108.66,15V95.59H200V.38Z",
  ],
})

export const VmHostIcon = createIcon({
  prefix: "fac",
  iconName: "vmHost",
  icon: [
    200,
    200,
    [],
    "0029",
    `M175.88,0H72.39C59.07,0,48.28,10.8,48.28,24.12v24.16H24.12C10.8,48.28,0,59.07,0,72.39v103.49
     C0,189.2,10.8,200,24.12,200h103.49c13.32,0,24.12-10.8,24.12-24.12v-24.16h24.16c13.32,0,24.12-10.8,24.12-24.12V24.12
     C200,10.8,189.2,0,175.88,0z`,
  ],
})

export const VmGuestIcon = createIcon({
  prefix: "fac",
  iconName: "vmGuest",
  icon: [
    200,
    200,
    [],
    "0028",
    `M175.88,0H72.39C59.07,0,48.28,10.8,48.28,24.12v24.16H24.12C10.8,48.28,0,59.07,0,72.39v103.49
     C0,189.2,10.8,200,24.12,200h103.49c13.32,0,24.12-10.8,24.12-24.12v-24.16h24.16c13.32,0,24.12-10.8,24.12-24.12V24.12
     C200,10.8,189.2,0,175.88,0z M121.63,156.54c0,7.38-5.99,13.37-13.37,13.37h-64.8c-7.38,0-13.37-5.99-13.37-13.37v-64.8
     c0-7.38,5.99-13.37,13.37-13.37h4.81v49.24c0,13.32,10.8,24.12,24.12,24.12h49.24V156.54z M121.63,121.63H91.74
     c-7.38,0-13.37-5.99-13.37-13.37V78.37h29.89c7.38,0,13.37,5.99,13.37,13.37V121.63z M169.91,108.26c0,7.38-5.99,13.37-13.37,13.37
     h-4.81V72.39c0-13.32-10.8-24.12-24.12-24.12H78.37v-4.81c0-7.38,5.99-13.37,13.37-13.37h64.8c7.38,0,13.37,5.99,13.37,13.37
     V108.26z`,
  ],
})

export const ServerIconLight = createIcon({
  prefix: "fac",
  iconName: "ServerLight",
  icon: [
    512,
    512,
    [],
    "f223",
    "M376 256c0-13.255 10.745-24 24-24s24 10.745 24 24-10.745 24-24 24-24-10.745-24-24zm-40 24c13.255 0 24-10.745 24-24s-10.745-24-24-24-24 10.745-24 24 10.745 24 24 24zm176-128c0 12.296-4.629 23.507-12.232 32 7.603 8.493 12.232 19.704 12.232 32v80c0 12.296-4.629 23.507-12.232 32 7.603 8.493 12.232 19.704 12.232 32v80c0 26.51-21.49 48-48 48H48c-26.51 0-48-21.49-48-48v-80c0-12.296 4.629-23.507 12.232-32C4.629 319.507 0 308.296 0 296v-80c0-12.296 4.629-23.507 12.232-32C4.629 175.507 0 164.296 0 152V72c0-26.51 21.49-48 48-48h416c26.51 0 48 21.49 48 48v80zm-480 0c0 8.822 7.178 16 16 16h416c8.822 0 16-7.178 16-16V72c0-8.822-7.178-16-16-16H48c-8.822 0-16 7.178-16 16v80zm432 48H48c-8.822 0-16 7.178-16 16v80c0 8.822 7.178 16 16 16h416c8.822 0 16-7.178 16-16v-80c0-8.822-7.178-16-16-16zm16 160c0-8.822-7.178-16-16-16H48c-8.822 0-16 7.178-16 16v80c0 8.822 7.178 16 16 16h416c8.822 0 16-7.178 16-16v-80zm-80-224c13.255 0 24-10.745 24-24s-10.745-24-24-24-24 10.745-24 24 10.745 24 24 24zm-64 0c13.255 0 24-10.745 24-24s-10.745-24-24-24-24 10.745-24 24 10.745 24 24 24zm64 240c-13.255 0-24 10.745-24 24s10.745 24 24 24 24-10.745 24-24-10.745-24-24-24zm-64 0c-13.255 0-24 10.745-24 24s10.745 24 24 24 24-10.745 24-24-10.745-24-24-24z",
  ],
})

// Devices
export const LinuxIcon = createIcon(brandIcons.faLinux)
export const WindowsIcon = createIcon(brandIcons.faWindows)
export const AppleIcon = createIcon(brandIcons.faApple)
export const CloudMonitorIcon = createIcon(solidIcons.faCloud)
export const NmsIcon = createIcon(solidIcons.faGlobe)
export const AndroidIcon = createIcon(brandIcons.faAndroid)
